import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useGetEventById, useUpdateEvent } from '../../../../../services/react-query/events';
import { Contact } from '../../../../../types/Contact';
import { eventBaseColor, EventEmployee, EventStatus } from '../../../../../types/Event';
import { Event } from '../../../../../types/Event';
import StatusPill from '../../../../../uikit/pills/StatusPill';
import { humanizeEventDateTimes } from '../../../../../utility/dateUtils';
import EmailLink from '../../../EmailLink/EmailLink';
import GeoLink from '../../../GeoLink/GeoLink';
import PhoneLink from '../../../PhoneLink/PhoneLink';
import DrawerSection from '../DrawerSection';
import InfoRow from '../InfoRow';
import ModuleDrawerHeader from '../ModuleDrawerHeader';
import TaskDrawerList from '../TaskDrawerList';
import { moduleDrawerStyles } from '../moduleDrawerStyles';
import _ from 'lodash';
import DrawerLink from '../DrawerLink';
import { UserConfigurableModule } from '../../../../../types/User';
import CustomFieldsDisplay from './CustomFieldsDisplay';
import { queryKeys } from '../../../../../services/react-query/queryKeys';
import { queryClient } from '../../../../../services/react-query/queryClient';
import StatusField from '../../../../tasks/StatusField';

type Props = { uuid: string };

const CenterSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={20} />
  </Box>
);

const EventDrawerView: React.FC<Props> = ({ uuid }) => {
  const { data: event, isLoading } = useGetEventById({ uuid });
  const { mutate: updateEvent } = useUpdateEvent({
    uuid,
    onSuccessCallback: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.allEvents] });
      setIsUpdating(false);
    },
  });

  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusChange = (status: EventStatus | null) => {
    if (!event || !status) return;

    setIsUpdating(true);
    updateEvent({ ...event, status });
  };
  if (isLoading) return <CenterSpinner />;
  if (!event)
    return (
      <Typography variant="body2" color="error">
        Event not found
      </Typography>
    );

  const contacts = event.contacts || [];
  const eventEmployees = event.event_employees || [];
  const tasks = event.tasks || [];
  const children = event.children || [];
  const formattedAddress = event.address?.formatted_address;

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(event.display_name)}</ModuleDrawerHeader>
      <Box sx={moduleDrawerStyles.content}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Event Information */}
          <DrawerSection title="Event Information">
            <InfoRow label="Name" value={event.display_name || event.name} />
            {event.multi_child && (
              <InfoRow
                label="Group"
                value={
                  <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.parent_uuid ?? ''}>
                    {event.parent_name}
                  </DrawerLink>
                }
              />
            )}
            <InfoRow label="Account" value={event.account_name} />
            <InfoRow label="Event Type" value={event.type_value} />
            <InfoRow label="Event Location" value={event.location_value} />
            {formattedAddress && <InfoRow label="Address" value={<GeoLink address={formattedAddress} />} />}
            <InfoRow label="Date / Time" value={humanizeEventDateTimes(event)} />
            <InfoRow
              label="Status"
              rightNode={
                <StatusField<EventStatus>
                  fullWidth
                  value={event.status}
                  options={_.values(EventStatus)}
                  getBackgroundColor={eventBaseColor}
                  renderOptionNode={(status) => <StatusPill status={status} />}
                  onChange={(_, value) => handleStatusChange(value)}
                  disabled={isUpdating}
                />
              }
            />
          </DrawerSection>

          {/* Custom Fields */}
          <CustomFieldsDisplay fields={event.fields} />
          {event.multi_parent && (
            <DrawerSection title="Subevents">
              {children.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No subevents found
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {_.map(children, (child: Event) => {
                    return (
                      <Box key={child.uuid} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, borderBottom: '1px solid #eee', py: 1 }}>
                        <DrawerLink module={UserConfigurableModule.EVENTS} uuid={child.uuid}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} title={child.display_name}>
                            {child.name}
                          </Typography>
                        </DrawerLink>

                        <InfoRow label="Type" value={child.type_value} />
                        <InfoRow label="Location" value={child.location_value} />
                        <InfoRow label="Date/Time" value={humanizeEventDateTimes(child)} />
                        <InfoRow label="Status" value={<StatusPill status={child.status} />} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DrawerSection>
          )}

          {/* Contacts */}
          <DrawerSection title="Contacts">
            {contacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No contacts found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {contacts.map((contact: Contact) => (
                  <Box key={contact.uuid}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {contact.full_name}
                    </Typography>
                    <InfoRow label="Email" value={<EmailLink email={contact.email} />} />
                    <InfoRow label="Phone" value={<PhoneLink phone={contact.phone_1} extension={contact.extension} />} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Employees */}
          <DrawerSection title="Employees">
            {eventEmployees.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No employees found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {eventEmployees.map((eventEmployee: EventEmployee) => (
                  <Box key={eventEmployee.employee.uuid}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {eventEmployee.employee.display_name}
                    </Typography>
                    <InfoRow label="Email" value={<EmailLink email={eventEmployee.employee.email} />} />
                    <InfoRow label="Phone" value={<PhoneLink phone={eventEmployee.employee.phone} extension={eventEmployee.employee.extension} />} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Tasks */}
          <DrawerSection title="Tasks">
            <TaskDrawerList tasks={tasks} />
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default EventDrawerView;
